import React, { FC, useEffect, useState } from 'react';
import { JimboResponse, Order, OrderProduct, OrderRefundRequest, ShippingAddress } from '@models';
import { useForm, SubmitHandler } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';
import { toCurrency } from '@helpers';

import {
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Button,
  TextField,
  FormControl,
  DialogActions,
  Typography,
} from '@mui/material';
import { useIssueOrderRefundMutation } from '@services';
import { showToast } from '@store/slices/notification';
import { addErrorToLog } from '@store/slices/error-log';
import { useAppDispatch, useAppSelector } from '@hooks/redux-custom';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import customer from '@store/slices/customer';

type Props = {
  order: Order;
  isOpen: boolean;
  orderProducts: OrderProduct[];
  onClose: () => void;
  orderShippingAddress?: ShippingAddress[];
};

type FormValues = {
  reason: string;
  amount: number;
};

const RefundTypes = ['Full Refund', 'Partial Refund'] as const;

type RefundType = typeof RefundTypes[number];

type Refund = {
  type: RefundType;
  reason: string;
  partialRefundAmount?: string;
};

const initialRefundState: Refund = { type: 'Full Refund', reason: '' };

const OrderRefundModal: FC<Props> = ({ order, isOpen, orderProducts, onClose, orderShippingAddress }) => {
  const [createOrderRefund, { isLoading }] = useIssueOrderRefundMutation();
  const { register, reset, handleSubmit } = useForm<FormValues>();
  const [refund, setRefund] = useState<Refund>(initialRefundState);
  const [refundError, setRefundError] = useState('');
  const customer = useAppSelector((state) => state.customer.value.selectedCustomer);

  const outstandingAmount = Number(order.total_inc_tax) - Number(order.refunded_amount);

  const refundValues =
    order.status !== 'Partially Refunded' ? RefundTypes : RefundTypes.filter((refundValue) => refundValue === 'Partial Refund');

  useEffect(() => {
    reset({
      amount: Number(order.total_inc_tax),
      reason: '',
    });
  }, [order, reset]);

  // reset the state when order refund modal closes
  useEffect(() => {
    if (!isOpen) {
      setRefund(initialRefundState);
    }
  }, [isOpen]);

  const dispatch = useAppDispatch();

  const handleRefundOptionChange = (event: SelectChangeEvent) => {
    // reset the state when switching refund options
    setRefund({ type: event.target.value as RefundType, reason: '' });
  };

  const handlePartialRefundAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRefund({ ...refund, partialRefundAmount: event.target.value });
  };

  const handleRefundReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRefund({ ...refund, reason: event.target.value });
    setRefundError('');
  };

  const onSubmit: SubmitHandler<FormValues> = (formData) => {
    if (refund) {
      const refundData: OrderRefundRequest = {
        customerId: customer.id,
        items: [],
        reason: formData.reason,
        payments: [
          {
            provider_id: 'custom',
            amount: refund.type === 'Full Refund' ? Number(formData.amount) : Number(refund?.partialRefundAmount),
            offline: true,
          },
        ],
      };

      if (refund.type === 'Full Refund') {
        refundData.items = orderProducts?.map((p) => ({
          item_type: 'PRODUCT',
          item_id: p.id,
          quantity: p.quantity,
        }));

        if (orderShippingAddress?.length && Number(orderShippingAddress[0]?.cost_inc_tax)) {
          refundData.items.push({
            item_type: 'SHIPPING',
            item_id: Number(orderShippingAddress[0]?.id),
            amount: Number(orderShippingAddress[0]?.cost_inc_tax),
            reason: formData.reason,
          });
        }
      } else {
        const isDiscountApplied = Number(order.coupon_discount) > 0;

        refundData.items = [
          {
            item_type: 'ORDER',
            item_id: order.id,
            amount: isDiscountApplied ? Number(order.total_inc_tax) - Number(order.refunded_amount) : Number(refund.partialRefundAmount),
          },
        ];

        if (isDiscountApplied) {
          refundData.merchant_calculated_override = {
            total_amount: Number(refund.partialRefundAmount),
            total_tax: 0,
          };
        }
      }

      if (refund.type === 'Full Refund' && !refund.reason) {
        setRefundError('Please enter a reason for full refund');
      } else if (refund.type === 'Partial Refund' && !refund.reason) {
        setRefundError('Please enter a reason for full refund');
      } else if ((refund.type === 'Partial Refund' && !refund.partialRefundAmount) || refund.partialRefundAmount === '0') {
        setRefundError('Please enter a valid amount');
      } else {
        createOrderRefund({ orderId: order.id, data: refundData })
          .unwrap()
          .then((data) => {
            dispatch(
              showToast({
                message: data.message || `Order #${order.id} successfully refunded.`,
                type: 'success',
              }),
            );

            onClose();
          })
          .catch((error: { data: JimboResponse }) => {
            dispatch(addErrorToLog(error?.data));

            dispatch(
              showToast({
                message: error.data?.message || `Failed to refund order #${order.id}.`,
                type: 'error',
              }),
            );
          });
      }
    }
  };

  const fullRefundErrorCheck = !refund.reason || refundError.length > 0;
  const hasError = refund?.type === 'Partial Refund' ? !refund.partialRefundAmount || fullRefundErrorCheck : fullRefundErrorCheck;

  return (
    <div>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth="lg"
        data-testid="order-refund-modal"
        PaperProps={{
          style: { borderRadius: 10 },
        }}
      >
        <DialogTitle>Refund - Order #{order.id}</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit(onSubmit)} id="refund-form">
            <FormControl fullWidth>
              {order.status === 'Partially Refunded' && (
                <Typography variant="body2">Full refunds are not available for partially refunded orders</Typography>
              )}
              <Select
                labelId="select-refund"
                id="refund-select"
                onChange={handleRefundOptionChange}
                data-testid="refund-option-select"
                value={refund?.type}
                defaultValue={refundValues[0]}
              >
                {refundValues.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {refund && (
              <FormControl sx={{ my: 2 }} fullWidth>
                <div data-testid="full-refund-view">
                  <Typography variant="body1" data-testid="full-refund-amount-value">
                    Amount: {refund.type === 'Full Refund' ? toCurrency(Number(order.total_inc_tax)) : toCurrency(outstandingAmount)}
                  </Typography>
                  <br />
                  {refund?.type === 'Partial Refund' && (
                    <TextField
                      sx={{ pb: 2, display: 'flex' }}
                      type="decimal"
                      label="Refund amount"
                      placeholder="0.00"
                      inputProps={{ pattern: '^[0-9]*(.[0-9]{0,2})?$', min: 0.0 }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      value={refund.partialRefundAmount}
                      onChange={handlePartialRefundAmountChange}
                      error={hasError}
                      helperText={refundError}
                    />
                  )}
                  <FormControl fullWidth>
                    <TextField
                      {...register('reason')}
                      label="Refund Reason*"
                      value={refund.reason}
                      onChange={handleRefundReasonChange}
                      error={hasError}
                      helperText={refundError}
                    />
                  </FormControl>
                </div>
              </FormControl>
            )}
          </form>
        </DialogContent>
        <DialogActions sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="button" onClick={onClose} sx={{ mr: 1 }}>
            Cancel
          </Button>
          <LoadingButton
            loading={isLoading}
            variant="contained"
            type="submit"
            form="refund-form"
            disabled={hasError}
            data-testid="order-refund-confirm-btn"
          >
            Confirm
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default OrderRefundModal;
